// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for all derived player statistics.
//
// Used by BOTH the website (via the `@shared/*` alias, re-exported from
// src/utils/player-stats.ts) and the Deno edge functions (imported with an
// explicit `.ts` extension).
//
// HARD RULE: this file must stay dependency-free. No imports from `src/`,
// no imports from a function directory, no third-party modules — Deno and
// Vite resolve those differently.
// ---------------------------------------------------------------------------

/** Minimum shots before an accuracy counts as a record / highlight. */
export const MIN_SHOTS_FOR_ACCURACY = 50;
/** Minimum hits taken before a K/D counts as a record / highlight. */
export const MIN_TAKEN_FOR_KD = 10;

/** Minimal structural types — the frontend types are compatible by shape. */
export interface StatsPlayer {
  weste: string;
  name: string;
  team?: string;
  score: string;
  shots: string;
  accurancy?: string;
}

export interface StatsTeam {
  team: string;
  score: string;
}

export interface StatsHitRow {
  player: string;
  targets: { [key: string]: number };
}

export interface PlayerStats {
  /** Only present when hit data exists for the game. */
  hitsDealt?: number;
  hitsTaken?: number;
  /** Undefined when no hit data, or when hitsTaken === 0 (=> unbeaten). */
  kd?: number;
  unbeaten?: boolean;
  nemesis?: { name: string; hits: number };
  favoriteVictim?: { name: string; hits: number };
  /** Undefined when shots === 0. */
  pointsPerShot?: number;
  /** Percent share of the own team score; undefined when no teams / non-positive score. */
  teamSharePercent?: number;
}

export const normalizeTeamName = (teamName: unknown): string => {
  const name = String(teamName ?? '').trim();
  return name.replace(/\s+team$/i, '');
};

/** True when the game actually has a meaningful team name (not free-for-all). */
export const hasTeamName = (teamName: unknown): boolean => {
  const name = normalizeTeamName(teamName).toLowerCase();
  return name !== '' && name !== 'unknown' && name !== 'none' && name !== '0';
};

const round1 = (value: number) => Math.round(value * 10) / 10;

export const statsKey = (p: { weste: string; name: string }) => `${p.weste}-${p.name}`;

/**
 * Rows of the hit matrix are addressed by player NAME, columns by weste number.
 * Everything below resolves both sides through explicit lookups instead of order.
 */
export const computePlayerStats = (
  players: StatsPlayer[],
  hitData: StatsHitRow[] | null | undefined,
  teams?: StatsTeam[] | null,
): Map<string, PlayerStats> => {
  const result = new Map<string, PlayerStats>();
  const hasHits = Array.isArray(hitData) && hitData.length > 0;

  const rowByName = new Map<string, StatsHitRow>();
  if (hasHits) {
    hitData!.forEach((row) => {
      if (!rowByName.has(row.player)) rowByName.set(row.player, row);
    });
  }

  // Totals dealt per player name — needed for the tie-break rule.
  const dealtByName = new Map<string, number>();
  if (hasHits) {
    players.forEach((p) => {
      const row = rowByName.get(p.name);
      if (!row) return;
      let sum = 0;
      Object.entries(row.targets || {}).forEach(([weste, hits]) => {
        if (weste === p.weste) return; // diagonal never counts
        sum += Number(hits) || 0;
      });
      dealtByName.set(p.name, sum);
    });
  }

  const teamScore = new Map<string, number>();
  (teams || []).forEach((t) => {
    const key = normalizeTeamName(t.team).toLowerCase();
    teamScore.set(key, parseInt(t.score, 10) || 0);
  });

  /** Stable pick: highest hits, then highest total dealt, then first in player order. */
  const pickBest = (candidates: { name: string; hits: number }[]) => {
    let best: { name: string; hits: number } | undefined;
    candidates.forEach((c) => {
      if (c.hits <= 0) return;
      if (!best) {
        best = c;
        return;
      }
      if (c.hits > best.hits) {
        best = c;
        return;
      }
      if (c.hits === best.hits) {
        const a = dealtByName.get(c.name) ?? 0;
        const b = dealtByName.get(best.name) ?? 0;
        if (a > b) best = c;
      }
    });
    return best;
  };

  players.forEach((player) => {
    const stats: PlayerStats = {};

    const score = parseInt(player.score, 10) || 0;
    const shots = parseInt(player.shots, 10) || 0;
    if (shots > 0) stats.pointsPerShot = round1(score / shots);

    if (hasTeamName(player.team)) {
      const total = teamScore.get(normalizeTeamName(player.team).toLowerCase());
      if (total && total > 0) stats.teamSharePercent = Math.round((score / total) * 100);
    }

    if (hasHits) {
      const row = rowByName.get(player.name);

      const dealt = dealtByName.get(player.name) ?? 0;

      // Column sum: every other player's row, looked up at THIS player's weste.
      let taken = 0;
      const attackers: { name: string; hits: number }[] = [];
      players.forEach((other) => {
        if (other.name === player.name && other.weste === player.weste) return;
        const otherRow = rowByName.get(other.name);
        if (!otherRow) return;
        const hits = Number(otherRow.targets?.[player.weste]) || 0;
        taken += hits;
        attackers.push({ name: other.name, hits });
      });

      stats.hitsDealt = dealt;
      stats.hitsTaken = taken;
      if (taken === 0) stats.unbeaten = true;
      else stats.kd = round1(dealt / taken);

      stats.nemesis = pickBest(attackers);

      if (row) {
        const victims: { name: string; hits: number }[] = [];
        players.forEach((other) => {
          if (other.weste === player.weste) return;
          const hits = Number(row.targets?.[other.weste]) || 0;
          victims.push({ name: other.name, hits });
        });
        stats.favoriteVictim = pickBest(victims);
      }
    }

    result.set(statsKey(player), stats);
  });

  return result;
};

export const formatDe1 = (v: number) =>
  v.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Website form: long word for an unbeaten player. */
export const formatKd = (stats: PlayerStats) => {
  if (stats.unbeaten) return 'unbesiegt';
  if (stats.kd === undefined) return '–';
  return formatDe1(stats.kd);
};

/** PDF-specific short form of the K/D value (narrow table column). */
export const formatKdShort = (stats: PlayerStats | undefined) => {
  if (!stats) return '';
  if (stats.unbeaten) return 'unbes.';
  if (stats.kd === undefined) return '';
  return formatDe1(stats.kd);
};

export const formatPointsPerShot = (stats: PlayerStats | undefined) => {
  if (!stats || stats.pointsPerShot === undefined) return '';
  return formatDe1(stats.pointsPerShot);
};
